/**
 * AuthGuard component
 * Redirects unauthenticated users to home page
 * Exempts home page and room pages from redirect
 */

import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../../lib/auth';

interface AuthGuardProps {
  children: React.ReactNode;
}

const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100%'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid var(--brand-color, #2090ff)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const isPublicPage = (pathname: string): boolean => {
  return (
    pathname === '/' ||
    pathname === '/auth' ||
    pathname === '/oidc-callback' ||
    pathname === '/recording-template' ||
    // Connection diagnostics must stay reachable when auth itself is broken
    pathname === '/_diagnostics' ||
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/rooms/')
  );
};

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const router = useRouter();
  const { isAuthenticated, isLoading, isInitialized } = useAuth();
  const isProtected = !isPublicPage(router.pathname);

  useEffect(() => {
    // Wait for auth to initialize
    if (!isInitialized || isLoading) {
      return;
    }

    // Redirect to home if not authenticated and on protected page
    if (!isAuthenticated && isProtected) {
      router.replace('/');
    }
  }, [isAuthenticated, isLoading, isInitialized, isProtected, router]);

  // Show loading spinner for protected pages while loading or redirecting
  if (isProtected && (!isInitialized || isLoading || !isAuthenticated)) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};
